import { Link, useLocation, useNavigate } from "react-router-dom";
import { Mail, MapPin, Phone, MessageCircle, Twitter, Music2 } from "lucide-react";
import { localizePath } from "@/i18n/translations";
import { PUBLIC_PHONE_LINK, WHATSAPP_URL } from "@/lib/contact";
import { scrollPageToTop, scrollToElementById } from "@/lib/scroll";

export const PublicFooter = ({ lang, copy }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const homePath = localizePath(lang);
  const branchesPath = localizePath(lang, "/branches");
  const contactPath = localizePath(lang, "/contact");
  const searchPath = localizePath(lang, "/search");

  const quickLinks = [
    { key: "home", label: copy.footer.quickLinks.home, type: "home" },
    { key: "map", label: copy.footer.quickLinks.map, type: "map" },
    { key: "branches", label: copy.footer.quickLinks.branches, type: "branches" },
    { key: "contact", label: copy.footer.quickLinks.contact, type: "contact" },
    { key: "search", label: copy.footer.quickLinks.advancedSearch, type: "search" },
  ];

  const handleQuickLink = (type) => {
    if (type === "home") {
      if (location.pathname === homePath) {
        scrollPageToTop();
        return;
      }

      navigate(homePath, { state: { scrollTarget: "top" } });
      return;
    }

    if (type === "map") {
      if (location.pathname === homePath) {
        scrollToElementById("map");
        return;
      }

      navigate(homePath, { state: { scrollTarget: "map" } });
      return;
    }

    if (type === "branches") {
      if (location.pathname === branchesPath) {
        scrollPageToTop();
        return;
      }

      navigate(branchesPath, { state: { scrollTarget: "top" } });
      return;
    }

    if (type === "contact") {
      if (location.pathname === contactPath) {
        scrollPageToTop();
        return;
      }

      navigate(contactPath, { state: { scrollTarget: "top" } });
      return;
    }

    if (type === "search") {
      if (location.pathname === searchPath) {
        scrollPageToTop();
        return;
      }

      navigate(searchPath, { state: { scrollTarget: "top" } });
    }
  };

  return (
    <footer className="public-footer" id="public-contact-footer" data-testid="public-footer-root">
      <div className="mx-auto grid max-w-7xl gap-8 px-4 py-10 sm:px-10 lg:grid-cols-3" data-testid="public-footer-main-grid">
        <section className="space-y-3" data-testid="public-footer-company-block">
          <h3 className="brand-gradient text-xl font-semibold" data-testid="public-footer-company-name">
            {copy.nav.brand}
          </h3>
          <p className="text-sm text-[#f3dfc2]" data-testid="public-footer-company-description">
            {copy.footer.companyDescription}
          </p>
        </section>

        <section className="space-y-3" data-testid="public-footer-quick-links-block">
          <h4 className="text-sm font-semibold text-[#fff2dc]" data-testid="public-footer-quick-links-title">
            {copy.footer.quickLinks.title}
          </h4>
          <div className="grid gap-2" data-testid="public-footer-quick-links-list">
            {quickLinks.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => handleQuickLink(item.type)}
                className="w-fit text-sm text-[#eed4ad] transition hover:text-[#fff2dc]"
                data-testid={`public-footer-quick-link-${item.key}`}
              >
                {item.label}
              </button>
            ))}
          </div>
        </section>

        <section className="space-y-3" data-testid="public-footer-contact-block">
          <h4 className="text-sm font-semibold text-[#fff2dc]" data-testid="public-footer-contact-title">
            {copy.footer.contactTitle}
          </h4>

          <a href={PUBLIC_PHONE_LINK} className="footer-contact-item" data-testid="public-footer-contact-phone">
            <Phone size={15} />
            <span>+966 507336999</span>
          </a>

          <a href={WHATSAPP_URL} className="footer-contact-item" target="_blank" rel="noreferrer" data-testid="public-footer-contact-whatsapp">
            <MessageCircle size={15} />
            <span>WhatsApp</span>
          </a>

          <a href="mailto:info@aamal-aqar.sa" className="footer-contact-item" data-testid="public-footer-contact-email">
            <Mail size={15} />
            <span>info@aamal-aqar.sa</span>
          </a>

          <p className="footer-contact-item" data-testid="public-footer-contact-location">
            <MapPin size={15} />
            <span>{copy.footer.location}</span>
          </p>

          <div className="flex items-center gap-2 pt-1" data-testid="public-footer-social-icons">
            <a
              href="https://x.com/groupaqar?lang=ar"
              className="footer-social-chip"
              aria-label="X"
              target="_blank"
              rel="noreferrer"
              data-testid="public-footer-social-x"
            >
              <Twitter size={14} />
            </a>
            <a
              href="https://www.tiktok.com/@groupaqar?lang=ar"
              className="footer-social-chip"
              aria-label="TikTok"
              target="_blank"
              rel="noreferrer"
              data-testid="public-footer-social-tiktok"
            >
              <Music2 size={14} />
            </a>
          </div>
        </section>
      </div>

      <div className="border-t border-[#b88f58]/30" data-testid="public-footer-legal-row">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-2 px-4 py-4 text-xs text-[#e5cda6] sm:px-10">
          <span data-testid="public-footer-copyright">{copy.footer.legal.copyright}</span>
          <div className="flex items-center gap-3" data-testid="public-footer-legal-links">
            <Link to="#" data-testid="public-footer-privacy-link">{copy.footer.legal.privacy}</Link>
            <Link to="#" data-testid="public-footer-terms-link">{copy.footer.legal.terms}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
